import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, Check, Download, Plus, Trash2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import AppShell from "@/components/app/AppShell";
import TimesheetsList from "@/pages/TimesheetsList";

const StatusPill = ({ status }) => {
    const isCert = status === "certified";
    return (
        <span
            className={`chip ${isCert ? "chip-certified" : "chip-draft"}`}
            data-testid="report-status-pill"
        >
            {isCert && <Check size={11} strokeWidth={2.5} />}
            {isCert ? "Certified" : "Draft"}
        </span>
    );
};

const formatUpdated = (iso) => {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        return d.toLocaleString(undefined, {
            year: "numeric",
            month: "short",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return iso;
    }
};

const HomeTabs = ({ tab, onChange }) => (
    <div
        className="tab-pill-group inline-flex mb-6"
        role="tablist"
        data-testid="home-tabs"
    >
        <button
            type="button"
            className="tab-pill"
            data-active={tab === "reports" || undefined}
            onClick={() => onChange("reports")}
            data-testid="home-tab-reports"
            role="tab"
            aria-selected={tab === "reports"}
        >
            Reports
        </button>
        <button
            type="button"
            className="tab-pill"
            data-active={tab === "timesheets" || undefined}
            onClick={() => onChange("timesheets")}
            data-testid="home-tab-timesheets"
            role="tab"
            aria-selected={tab === "timesheets"}
        >
            Timesheets
        </button>
    </div>
);

const ReportsListContent = () => {
    const [reports, setReports] = useState(null);
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);
    const nav = useNavigate();

    const load = async () => {
        try {
            const { data } = await api.get("/reports");
            setReports(data);
        } catch (err) {
            setError(formatApiError(err));
            setReports([]);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const createDraft = async () => {
        setBusy(true);
        setError("");
        try {
            const { data } = await api.post("/reports", {});
            nav(`/reports/${data.id}/edit`);
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    const deleteReport = async (id) => {
        if (!confirm("Delete this draft report?")) return;
        try {
            await api.delete(`/reports/${id}`);
            setReports((prev) => prev.filter((r) => r.id !== id));
        } catch (err) {
            setError(formatApiError(err));
        }
    };

    return (
        <>
            <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
                <div>
                    <h1
                        className="text-3xl sm:text-4xl font-semibold text-slate-900"
                        data-testid="reports-page-title"
                    >
                        My reports
                    </h1>
                    <p className="text-[14px] text-slate-600 mt-1">
                        Every draft you've started and every report you've certified.
                    </p>
                </div>
                <button
                    onClick={createDraft}
                    disabled={busy}
                    className="btn btn-primary"
                    data-testid="reports-new-button"
                >
                    <Plus size={16} strokeWidth={2.5} />
                    New report
                </button>
            </div>

            {error && (
                <div
                    className="err rounded-[10px] border border-red-200 bg-red-50/70 px-3 py-2 mb-4"
                    data-testid="reports-error"
                >
                    {error}
                </div>
            )}

            {reports === null && (
                <div className="glass-card text-[13px] text-slate-500">Loading…</div>
            )}

            {reports && reports.length === 0 && (
                <div className="glass-card" data-testid="reports-empty-state">
                    <div className="flex flex-col items-start gap-4">
                        <div className="text-[13px] text-slate-500">Nothing here yet</div>
                        <h2 className="text-2xl font-semibold text-slate-900">
                            Start your first report
                        </h2>
                        <p className="text-[14px] text-slate-600 max-w-md">
                            Start a new draft. Autosave engages on the first blur — you can walk
                            away and it stays.
                        </p>
                        <button
                            onClick={createDraft}
                            className="btn btn-primary"
                            data-testid="reports-empty-new-button"
                        >
                            <Plus size={16} strokeWidth={2.5} />
                            Create the first report
                            <ArrowRight size={16} strokeWidth={2} />
                        </button>
                    </div>
                </div>
            )}

            {reports && reports.length > 0 && (
                <>
                    <div className="mobile-only flex flex-col gap-3" data-testid="reports-mobile-list">
                        {reports.map((r) => {
                            const to = r.status === "certified" ? `/reports/${r.id}` : `/reports/${r.id}/edit`;
                            return (
                                <Link
                                    key={r.id}
                                    to={to}
                                    className="glass-card !py-4 !px-4 block active:opacity-80"
                                    data-testid={`report-card-${r.id}`}
                                >
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="min-w-0 flex-1">
                                            <div className="text-[17px] font-semibold text-slate-900 truncate">
                                                {r.job_number || <span className="text-slate-400">Untitled</span>}
                                            </div>
                                            <div className="text-[12px] text-slate-500 mt-0.5">
                                                {r.report_date || "—"}
                                                {r.report_date && (
                                                    <span> · {new Date(r.report_date + "T00:00:00").toLocaleDateString(undefined, { weekday: "long" })}</span>
                                                )}
                                            </div>
                                            <div className="mt-2"><StatusPill status={r.status} /></div>
                                        </div>
                                        <div className="text-right shrink-0">
                                            <div className="text-[11px] text-slate-400 uppercase tracking-wider">Crew hrs</div>
                                            <div className="text-[15px] font-medium tabular-nums text-slate-900">
                                                {r.crew_total_hours == null ? "—" : r.crew_total_hours}
                                            </div>
                                        </div>
                                    </div>
                                </Link>
                            );
                        })}
                    </div>

                    <div className="glass-card !p-0 overflow-hidden desktop-only">
                        <div className="overflow-x-auto">
                            <table className="g-table" data-testid="reports-table">
                                <thead>
                                    <tr>
                                        <th>Job #</th>
                                        <th>Date</th>
                                        <th>Day</th>
                                        <th>Crew hrs</th>
                                        <th>Status</th>
                                        <th>Updated</th>
                                        <th className="text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {reports.map((r) => (
                                        <tr key={r.id} data-testid={`report-row-${r.id}`}>
                                            <td className="font-medium">
                                                {r.job_number || (
                                                    <span className="text-slate-400">Untitled</span>
                                                )}
                                            </td>
                                            <td>{r.report_date || <span className="text-slate-400">—</span>}</td>
                                            <td>
                                                {r.report_date
                                                    ? new Date(r.report_date + "T00:00:00").toLocaleDateString(
                                                          undefined,
                                                          { weekday: "long" }
                                                      )
                                                    : <span className="text-slate-400">—</span>}
                                            </td>
                                            <td className="tabular-nums">
                                                {r.crew_total_hours == null ? (
                                                    <span className="text-slate-400">—</span>
                                                ) : (
                                                    r.crew_total_hours
                                                )}
                                            </td>
                                            <td>
                                                <StatusPill status={r.status} />
                                            </td>
                                            <td className="text-slate-500 text-[13px]">
                                                {formatUpdated(r.updated_at)}
                                            </td>
                                            <td>
                                                <div className="flex gap-2 justify-end">
                                                    {r.status === "certified" ? (
                                                        <>
                                                            <Link
                                                                to={`/reports/${r.id}`}
                                                                className="btn btn-secondary btn-sm"
                                                                data-testid={`report-view-${r.id}`}
                                                            >
                                                                View
                                                            </Link>
                                                            {r.pdf_url && (
                                                                <a
                                                                    href={r.pdf_url}
                                                                    target="_blank"
                                                                    rel="noreferrer"
                                                                    className="btn btn-primary btn-sm btn-icon"
                                                                    title="Download signed PDF"
                                                                    data-testid={`report-download-${r.id}`}
                                                                    aria-label="Download PDF"
                                                                >
                                                                    <Download size={14} strokeWidth={2} />
                                                                </a>
                                                            )}
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Link
                                                                to={`/reports/${r.id}/edit`}
                                                                className="btn btn-secondary btn-sm"
                                                                data-testid={`report-edit-${r.id}`}
                                                            >
                                                                Edit
                                                            </Link>
                                                            <button
                                                                onClick={() => deleteReport(r.id)}
                                                                className="btn btn-danger btn-sm btn-icon"
                                                                title="Delete draft"
                                                                data-testid={`report-delete-${r.id}`}
                                                                aria-label="Delete"
                                                            >
                                                                <Trash2 size={14} strokeWidth={2} />
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            )}

            <button
                type="button"
                onClick={createDraft}
                disabled={busy}
                className="mobile-fab mobile-only"
                aria-label="New report"
                data-testid="reports-new-fab"
                style={{ bottom: "calc(24px + env(safe-area-inset-bottom))" }}
            >
                <Plus size={22} strokeWidth={2.5} />
            </button>
        </>
    );
};

const ReportsPage = () => {
    const [params, setParams] = useSearchParams();
    const tab = params.get("tab") === "timesheets" ? "timesheets" : "reports";

    const setTab = (next) => {
        const nextParams = new URLSearchParams(params);
        if (next === "reports") nextParams.delete("tab");
        else nextParams.set("tab", next);
        setParams(nextParams, { replace: true });
    };

    return (
        <AppShell>
            <HomeTabs tab={tab} onChange={setTab} />
            {tab === "reports" ? <ReportsListContent /> : <TimesheetsList />}
        </AppShell>
    );
};

export default ReportsPage;
